import * as d3 from 'd3';

import { SvgManager } from '../../charting/SvgManager';
import { DependencyDirection } from './DependencyDirection';
import { DependencyConfiguration } from './DependencyConfiguration';

export class Dependencies<T> {
    _labels: { expand: string; collapse: string };
    private _initialLevel: number = 1;
    private _container: d3.Selection<any>;
    private _width: number;
    private _height: number;
    private _margin = 25;
    private _layout: d3.layout.Tree<any>;
    private _projection: d3.svg.Diagonal<d3.svg.diagonal.Link<d3.svg.diagonal.Node>, d3.svg.diagonal.Node>;
    private _data: T;
    private _direction: DependencyDirection;
    private _childrenCallback: (d: T) => any[];
    private _clickCallback: (d: T) => void;
    private _nameCallback = (d: any) => d.name;
    private _thicknessScale = d3.scale.linear().range([1, 5]);
    private _thicknessCallback = d => 1;
    dispatch = d3.dispatch('onNodeClick');

    constructor(manager: SvgManager) {
        const container = manager.container();
        this._width = manager.getSize().width;
        this._height = manager.getSize().height;
        this.init(container);
        manager.addResizeFunction((w, h) => {
            this.resize(w, h);
        });
    }

    private resize(w: number, h: number) {
        this._width = w;
        this._height = h;
        const height = h - 2 * this._margin;
        this._layout.size([w - 2 * this._margin, height]);
        this.layout();
    }

    private layout() {
        const nodes = this._data ? this._layout.nodes(this._data) : [];
        const pointsSelection = this._container
            .select('.series')
            .selectAll('.point')
            .data(nodes);
        this.refreshPoints(pointsSelection);
        this.setProjection();
        this._container
            .select('.links')
            .selectAll('.link')
            .data(this._layout.links(nodes))
            .attr('d', this._projection);
    }

    private init(container: d3.Selection<any>) {
        this._childrenCallback = (d: any) => d.children;
        this._container = container.append('g')
            .classed('chart', true)
            .attr('transform', `translate(${this._margin},${this._margin})`);
        this._container
            .append('g')
            .classed('links', true);
        this._container.append('g')
            .classed('series', true);
        this._layout = d3.layout.tree()
            .size([this._width - 2 * this._margin, this._height - 2 * this._margin]);
        this._projection = d3.svg.diagonal();
        this._direction = DependencyDirection.BottomToTop;
        this.setProjection();
    }

    private setProjection() {
        this._projection
            .projection(d => this.getPos(d));
    }

    private getPos(d: any): [number, number] {
        let y = d.y;
        switch (this._direction) {
            case DependencyDirection.TopToBottom:
                y = d.y;
                break;
            case DependencyDirection.BottomToTop:
                y = this._height - 2 * this._margin - d.y;
                break;
        }
        return [d.x, y];
    }

    update(data: T) {
        this.mapChildren(data);
        this._data = data;
        this.render(this._data);
    }

    private render(data) {
        const nodes = this._layout.nodes(this._data);
        const dataBound = this._container
            .select('.series')
            .selectAll('.point')
            .data(nodes);
        dataBound.exit()
            .transition()
            .attr('transform', d => {
                let source = d;
                if (d.parent) {
                    source = d.parent;
                }
                const pos = this.getPos(source);
                return `translate(${pos[0]},${pos[1]})`;
            })
            .remove();
        const enterSelection = dataBound.enter()
            .append('g')
            .classed('point', true)
            .attr('transform', d => {
                let source = d;
                if (d.parent) {
                    source = d.parent;
                }
                const pos = this.getPos(source);
                return `translate(${pos[0]},${pos[1]})`;
            });
        enterSelection.append('circle')
            .attr('r', 4)
            .classed('node', true)
            .on('click', d => {
                if (d.children) {
                    d._children = d.children;
                    d.children = null;
                } else {
                    d.children = d._children;
                    d._children = null;
                }
                this.render(d);
                this.dispatch.onNodeClick(d);
            })
            .append('title');

        this.refreshPoints(dataBound);
        this.drawLinks(this._container, this._layout, nodes);
    }

    private appendText(selection: d3.Selection<any>) {
        return selection.append('g')
            .classed('text-group', true)
            .each(function (d) {
                if (d.attributes) {
                    d3.select(this).attr(d.attributes);
                }
            })
            .on('click', (d) => {
                if (this._clickCallback) {
                    this._clickCallback(d);
                }
            })
            .append('text')
            .style('text-anchor', 'middle')
            .attr('x', 0)
            .attr('y', (d, i) => (i % 2 === 0 ? 20 : -10))
            .classed('clickable', !!this._clickCallback)
            .text((d: any) => this._nameCallback(d));
    }

    private refreshPoints(selection: d3.Selection<any>) {
        selection
            .transition()
            .attr('transform', d => {
                const pos = this.getPos(d);
                return `translate(${pos[0]},${pos[1]})`;
            });
        selection.select('.node')
            .classed('empty', d => {
                const children = this._childrenCallback(d);
                return !(children && children.length);
            })
            .select('title')
            .text(d => {
                const children = this._childrenCallback(d);
                let text = '';
                if (children && children.length) {
                    if (d.children) {
                        text = this._labels.collapse;
                    } else {
                        text = this._labels.expand;
                    }
                }
                return text;
            });
        selection
            .select('g.text-group').remove();
        this.appendText(selection);
    }

    private drawLinks(container: d3.Selection<any>,
        layout: d3.layout.Tree<d3.layout.tree.Node>,
        nodes: d3.layout.tree.Node[]) {
        const links = layout.links(nodes);
        const linkDataBound = container
            .select('.links')
            .selectAll('.link')
            .data(links);
        linkDataBound.exit()
            .transition()
            .attr('d', (d: any) => {
                const target = d.source;
                return this._projection({ source: d.source, target });
            })
            .remove();

        this._thicknessScale.domain(d3.extent(links, l => this._thicknessCallback(l.target)));

        linkDataBound.enter()
            .append('path')
            .classed('link', true)
            .attr('d', (d: any) => {
                const target = d.source;
                return this._projection({ source: d.source, target });
            });
        linkDataBound
            .transition()
            .attr('d', this._projection)
            .style('stroke-width', d => {
                return this._thicknessScale(this._thicknessCallback(d.target));
            });
    }

    private mapChildren(d: any, depth = 0) {
        const children = this._childrenCallback(d);
        if (this._initialLevel && depth < this._initialLevel) {
            d.children = children;
        } else {
            d._children = children;
        }
        depth += 1;
        if (children) {
            children.forEach(c => this.mapChildren(c, depth));
        }
    }

    configuration(value?: DependencyConfiguration) {
        if (arguments.length) {
            this._childrenCallback = value.childrenCallback;
            this._direction = value.direction;
            this._clickCallback = value.clickCallback;
            this._nameCallback = value.nameCallback ? value.nameCallback : this._nameCallback;
            this._thicknessCallback = value.thicknessCallback ? value.thicknessCallback : this._thicknessCallback;
            this._initialLevel = value.initialLevel;
            this._labels = value.labels || { expand: 'Expand', collapse: 'Collapse' };
        }
    }
}